import React from 'react';
import { Skeleton as MuiSkeleton, Box } from '@mui/material';

interface SkeletonComponentProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  sx?: any;
}

/**
 * Skeleton de chargement avec animations fluides
 */
export const Skeleton: React.FC<SkeletonComponentProps> = ({
  variant = 'text',
  width,
  height,
  sx,
  ...props
}) => {
  const borderRadius = variant === 'rounded' ? 4 : variant === 'circular' ? '50%' : 2;

  return (
    <MuiSkeleton
      variant={variant}
      animation="wave"
      width={width}
      height={height}
      sx={{
        borderRadius,
        background: 'linear-gradient(90deg, rgba(18, 155, 175, 0.1) 0%, rgba(18, 155, 175, 0.2) 50%, rgba(18, 155, 175, 0.1) 100%)',
        backgroundSize: '200% 100%',
        '&': {
          animation: 'shimmer 1.5s infinite linear',
        },
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '-200% 0',
          },
          '100%': {
            backgroundPosition: '200% 0',
          },
        },
        ...sx,
      }}
      {...props}
    />
  );
};

/**
 * Skeleton pour carte de statistique
 */
export const StatCardSkeleton: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <Skeleton variant="circular" width={32} height={32} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="80%" height={20} />
  </Box>
);

/**
 * Skeleton pour liste d'éléments
 */
export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <Box>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ py: 2, borderBottom: i < count - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
    ))}
  </Box>
);

/**
 * Skeleton pour table
 */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Box>
    <Box sx={{ display: 'flex', gap: 2, mb: 2, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} variant="text" width={100} height={30} />
      ))}
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 2, py: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        {Array.from({ length: 6 }).map((_, j) => (
          <Skeleton key={j} variant="text" width={100} height={24} />
        ))}
      </Box>
    ))}
  </Box>
);

/**
 * Skeleton pour formulaire
 */
export const FormSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Skeleton variant="text" width="30%" height={24} />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
    <Skeleton variant="text" width="30%" height={24} />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
    <Skeleton variant="text" width="30%" height={24} />
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
      <Skeleton variant="rectangular" width="50%" height={48} sx={{ borderRadius: 3 }} />
      <Skeleton variant="rectangular" width="50%" height={48} sx={{ borderRadius: 3 }} />
    </Box>
  </Box>
);

export default Skeleton;
